import { api } from "./api";
import {
  TreatmentProtocol,
  TreatmentProtocolKind,
  TreatmentProtocolProgress,
  TreatmentProtocolSaveRequest,
  UserTreatmentProtocolHistoryEntry,
} from "../types/treatmentProtocol.types";

interface ListResponse {
  success: boolean;
  protocols: TreatmentProtocol[];
}

interface SingleResponse {
  success: boolean;
  protocol: TreatmentProtocol | null;
}

interface HistoryResponse {
  success: boolean;
  history: UserTreatmentProtocolHistoryEntry[];
}

interface ProgressResponse {
  success: boolean;
  progress: TreatmentProtocolProgress | null;
}

/**
 * Treatment protocol templates. The server scopes every call to the caller's company, so no
 * companyId is sent.
 */
export const treatmentProtocolsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTreatmentProtocols: build.query<
      TreatmentProtocol[],
      { kind?: TreatmentProtocolKind; includeInactive?: boolean } | void
    >({
      query: (args) => ({
        url: "/TreatmentProtocols",
        method: "GET",
        params: {
          ...(args?.kind ? { kind: args.kind } : {}),
          ...(args?.includeInactive ? { includeInactive: true } : {}),
        },
      }),
      transformResponse: (response: ListResponse) => response?.protocols ?? [],
      providesTags: ["TreatmentProtocols"],
    }),

    getTreatmentProtocol: build.query<TreatmentProtocol | null, number>({
      query: (protocolId) => ({
        url: `/TreatmentProtocols/${protocolId}`,
        method: "GET",
      }),
      transformResponse: (response: SingleResponse) => response?.protocol ?? null,
      providesTags: ["TreatmentProtocols"],
    }),

    /** The protocol assigned to a patient, used to pre-fill a new appointment. */
    getUserTreatmentProtocol: build.query<TreatmentProtocol | null, number>({
      query: (userId) => ({
        url: `/TreatmentProtocols/for-user/${userId}`,
        method: "GET",
      }),
      transformResponse: (response: SingleResponse) => response?.protocol ?? null,
      providesTags: ["UserTreatmentProtocol"],
    }),

    createTreatmentProtocol: build.mutation<
      TreatmentProtocol | null,
      TreatmentProtocolSaveRequest
    >({
      query: (body) => ({
        url: "/TreatmentProtocols",
        method: "POST",
        body,
      }),
      transformResponse: (response: SingleResponse) => response?.protocol ?? null,
      invalidatesTags: ["TreatmentProtocols"],
    }),

    updateTreatmentProtocol: build.mutation<
      TreatmentProtocol | null,
      { protocolId: number; body: TreatmentProtocolSaveRequest }
    >({
      query: ({ protocolId, body }) => ({
        url: `/TreatmentProtocols/${protocolId}`,
        method: "PUT",
        body,
      }),
      transformResponse: (response: SingleResponse) => response?.protocol ?? null,
      invalidatesTags: ["TreatmentProtocols", "UserTreatmentProtocol"],
    }),

    /** Deactivates the protocol and clears it from every patient it was assigned to. */
    deactivateTreatmentProtocol: build.mutation<{ success: boolean }, number>({
      query: (protocolId) => ({
        url: `/TreatmentProtocols/${protocolId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["TreatmentProtocols", "UserTreatmentProtocol", "user"],
    }),

    /**
     * Pass null to clear the patient's assignment. Assigning a different protocol closes the
     * current period and opens a new one, which is also what resets the treatment counter.
     */
    assignTreatmentProtocol: build.mutation<
      TreatmentProtocol | null,
      { userId: number; treatmentProtocolId: number | null; sessionsUntilRescan?: number | null }
    >({
      query: ({ userId, treatmentProtocolId, sessionsUntilRescan }) => ({
        url: `/TreatmentProtocols/assign/${userId}`,
        method: "PUT",
        body: { treatmentProtocolId, sessionsUntilRescan },
      }),
      transformResponse: (response: SingleResponse) => response?.protocol ?? null,
      invalidatesTags: ["TreatmentProtocols", "UserTreatmentProtocol"],
    }),

    /** Every protocol period the patient has been through, newest first. */
    getUserTreatmentProtocolHistory: build.query<UserTreatmentProtocolHistoryEntry[], number>({
      query: (userId) => ({
        url: `/TreatmentProtocols/assign/${userId}/history`,
        method: "GET",
      }),
      transformResponse: (response: HistoryResponse) => response?.history ?? [],
      providesTags: ["UserTreatmentProtocol"],
    }),

    /** How far the patient is into the current period, and which treatment an appointment is. */
    getTreatmentProtocolProgress: build.query<
      TreatmentProtocolProgress | null,
      { userId: number; appointmentId?: number }
    >({
      query: ({ userId, appointmentId }) => ({
        url: `/TreatmentProtocols/assign/${userId}/progress`,
        method: "GET",
        params: appointmentId ? { appointmentId } : {},
      }),
      transformResponse: (response: ProgressResponse) => response?.progress ?? null,
      providesTags: ["UserTreatmentProtocol"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTreatmentProtocolsQuery,
  useGetTreatmentProtocolQuery,
  useGetUserTreatmentProtocolQuery,
  useLazyGetUserTreatmentProtocolQuery,
  useCreateTreatmentProtocolMutation,
  useUpdateTreatmentProtocolMutation,
  useDeactivateTreatmentProtocolMutation,
  useAssignTreatmentProtocolMutation,
  useGetUserTreatmentProtocolHistoryQuery,
  useGetTreatmentProtocolProgressQuery,
} = treatmentProtocolsApi;
